import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import ProductCard from "@/components/ProductCard";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ChevronRight, 
  Shield, 
  Zap, 
  RefreshCw, 
  Headphones,
  Check,
  Star,
  Plane,
  Code,
  Users,
  Package
} from "lucide-react";

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          api.get("/products?featured=true"),
          api.get("/settings")
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 3));
        setSettings(settingsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Code,
      title: "Optimized Code",
      description: "Scripts optimized for low resource usage and high performance."
    },
    {
      icon: Shield,
      title: "Secure Purchase",
      description: "Safe payments with Tebex and instant delivery."
    },
    {
      icon: RefreshCw,
      title: "Free Updates",
      description: "Product updates and compatibility information are published with each script."
    },
    {
      icon: Headphones,
      title: "Product Support",
      description: "Documentation and support are available for installation and product questions."
    }
  ];

  const trustItems = [
    { icon: Check, text: "ESX & QBCore Compatible" },
    { icon: Check, text: "Detailed Documentation" },
    { icon: Check, text: "Easy Installation" },
    { icon: Check, text: "English Support" },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection settings={settings} />

      {/* Featured Products */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
              Featured Scripts
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Featured Products
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Purpose-built aviation systems for immersive FiveM servers
            </p>
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/products">
              <Button
                data-testid="view-all-products-btn"
                variant="outline"
                className="bg-transparent border border-primary/30 text-primary hover:bg-primary/10 rounded-lg uppercase tracking-widest px-8 py-6"
              >
                View All Products
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Runway Separator */}
      <div className="runway-separator max-w-4xl mx-auto px-6">
        <div className="line" />
        <div className="center" />
        <div className="line" />
      </div>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
                Why AeroCore?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
                Premium Quality, Professional Service
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                At AeroCore Scripts, we develop the highest quality aviation scripts for FiveM servers. 
                Every script is designed with realism, optimization, and user experience in mind.
              </p>

              {/* Trust Items */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {trustItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-primary/10 border border-primary/30 rounded-md flex items-center justify-center">
                      <item.icon className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <Link to="/about">
                <Button
                  data-testid="about-cta-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg border-l-4 border-accent uppercase tracking-widest font-bold px-6 glow-blue-hover"
                >
                  About Us
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Right Content - Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-950/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors group"
                >
                  <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center mb-4 group-hover:border-primary/60 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold tracking-wide mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {settings?.show_stats === true && (
        <section className="py-16 bg-slate-950/50 border-y border-slate-800/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
              {[
                { icon: Users, value: settings.stat_customers, label: settings.stat_customers_label },
                { icon: Package, value: settings.stat_scripts, label: settings.stat_scripts_label },
                { icon: Headphones, value: settings.stat_support, label: settings.stat_support_label },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="w-8 h-8 text-primary mx-auto mb-3 opacity-50" />
                  <div className="stat-counter text-2xl md:text-3xl">{stat.value}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative bg-gradient-to-b from-transparent to-primary/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
              Got Questions?
            </h2>
            <p className="text-muted-foreground mb-8">
              Join our Discord server and meet our community. 
              We're happy to answer all your questions.
            </p>
            {settings?.discord_url ? (
              <a href={settings.discord_url} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="cta-discord-btn"
                  variant="outline"
                  className="bg-transparent border border-primary/30 text-primary hover:bg-primary/10 rounded-lg uppercase tracking-widest px-8 py-6"
                >
                  Join Discord
                </Button>
              </a>
            ) : (
              <Link to="/contact">
                <Button variant="outline" className="rounded-lg border-primary/30 text-primary">
                  Contact Support
                </Button>
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
